import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { FiArrowUpRight, FiGrid, FiHome, FiList, FiMap, FiMapPin, FiMaximize2, FiRefreshCw } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import PropertyCard from './PropertyCard';
import ServiceAdCard from './ServiceAdCard';
import { demoServiceAds } from '../data/demoServiceAds';
import { getProperties } from '../services/propertiesService';
import { trackProductEvent } from '../services/productAnalytics';
import { rememberPropertySearch } from '../services/recommendationHistory';
const PropertyMap=lazy(()=>import('./map/PropertyMap'));

const firstFeature = (property, keys) => {
  const features = property.features || {};
  for (const key of keys) {
    const value = Number(features[key] ?? property[key]);
    if (Number.isFinite(value) && value > 0) return value;
  }
  return 0;
};

function CompactPropertyRow({ property, detailQuery = '' }) {
  const image = property.images?.find(Boolean);
  const address = property.address || property.neighborhood || property.department || 'Ubicación a confirmar';
  const context = [property.neighborhood, property.department].filter((value, index, values) => value && value !== address && values.indexOf(value) === index).join(' · ');
  const bedrooms = firstFeature(property, ['dormitorios', 'habitaciones', 'bedrooms', 'rooms']);
  const bathrooms = firstFeature(property, ['banos', 'baños', 'bathrooms']);
  const surface = firstFeature(property, ['metrosTotales', 'metrosCubiertos', 'surfaceCovered', 'surfaceTotal']);
  const price = new Intl.NumberFormat('es-AR', { style: 'currency', currency: property.currency || 'USD', maximumFractionDigits: 0 }).format(Number(property.price) || 0);
  const details = [bedrooms && `${bedrooms} dorm.`, bathrooms && `${bathrooms} baño${bathrooms === 1 ? '' : 's'}`, surface && `${surface} m²`].filter(Boolean);
  const params = new URLSearchParams(detailQuery);
  if (property.editorialCampaignId) { params.set('campaign', property.editorialCampaignId); params.set('source', 'EDITORIAL'); }
  const query = params.toString();
  return <CompactLink to={`/properties/${property._id}${query ? `?${query}` : ''}`} aria-label={`Ver ${property.title}`}>
    <CompactImage>{image ? <img src={image} alt="" loading="lazy" /> : <FiHome />}</CompactImage>
    <CompactContent>
      <CompactMeta>{property.operation?.replaceAll('_', ' ') || 'Propiedad'}</CompactMeta>
      <CompactTitle>{property.title || `${property.type || 'Propiedad'} en ${address}`}</CompactTitle>
      <CompactLocation><FiMapPin /> <span>{address}</span>{context && <small>{context}</small>}</CompactLocation>
      <CompactFooter><strong>{price}{property.operation === 'alquiler_vacacional' && <small> / noche</small>}{['alquiler', 'alquiler_temporal'].includes(property.operation) && <small> / mes</small>}</strong><span>{bedrooms > 0 && <FiHome />}{surface > 0 && <FiMaximize2 />}{details.join(' · ')}</span></CompactFooter>
    </CompactContent>
    <FiArrowUpRight className="open" aria-hidden="true" />
  </CompactLink>;
}

function PropertyList({ filters = {}, search, onFilterChange }) {
  const [properties, setProperties] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [page, setPage] = useState(1);
  const [mobileMapOpen, setMobileMapOpen] = useState(false);
  const [mobileListOpen, setMobileListOpen] = useState(false);
  const loadMoreRef = useRef(null);
  const [compact, setCompact] = useState(false);
  const [highlightedPropertyId, setHighlightedPropertyId] = useState(null);
  const [desktopMap,setDesktopMap]=useState(()=>window.matchMedia('(min-width: 781px)').matches);

  useEffect(()=>{
    const media=window.matchMedia('(min-width: 781px)');
    const update=()=>setDesktopMap(media.matches);
    media.addEventListener?.('change',update);
    return()=>media.removeEventListener?.('change',update);
  },[]);

  const baseQueryString = useMemo(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '' && value !== false && value !== 'todos') {
        params.set(key, String(value));
      }
    });
    if (search?.trim()) params.set('q', search.trim());
    return params.toString();
  }, [filters, search]);

  useEffect(() => setPage(1), [baseQueryString]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');

    const timer = window.setTimeout(() => {
      const params = new URLSearchParams(baseQueryString);
      params.set('limit', '12');
      params.set('page', String(page));
      getProperties(params.toString())
        .then((response) => {
          if (!active) return;
          const nextProperties = Array.isArray(response.data) ? response.data : [];
          const nextTotal = Number(response.total) || 0;

          setProperties((current) => page === 1
            ? nextProperties
            : [...current, ...nextProperties.filter((property) => !current.some((item) => item._id === property._id))]);
          setTotal(nextTotal);
          if (page === 1) {
            rememberPropertySearch(baseQueryString, nextTotal);
            trackProductEvent('SEARCH_RESULTS',{metadata:{total:nextTotal,filters:baseQueryString}});
          }
        })
        .catch(() => {
          if (!active) return;
          if (page === 1) { setProperties([]); setTotal(0); }
          setError('No pudimos cargar las propiedades. Revisá tu conexión e intentá nuevamente.');
        })
        .finally(() => active && setLoading(false));
    }, 260);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [baseQueryString, page, reloadKey, filters, search]);

  const hasMore = properties.length > 0 && properties.length < total;

  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || loading || error || !hasMore) return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      observer.disconnect();
      setPage((value) => value + 1);
    }, { rootMargin: '180px 0px' });
    observer.observe(target);
    return () => observer.disconnect();
  }, [error, hasMore, loading]);

  return (
    <ResultsSection>
      <ResultsShell>
        <ResultsHeader>
          <div>
            <ResultsEyebrow>PROPIEDADES</ResultsEyebrow>
            <ResultsTitle>
              {loading ? 'Buscando lugares…' : `${total} ${total === 1 ? 'propiedad encontrada' : 'propiedades encontradas'}`}
            </ResultsTitle>
          </div>
          <ViewControls>
            <SortSelect
              name="sort"
              value={filters.sort || 'newest'}
              onChange={onFilterChange}
              aria-label="Ordenar propiedades"
            >
              <option value="newest">Más recientes</option>
              <option value="price_asc">Menor precio</option>
              <option value="price_desc">Mayor precio</option>
              <option value="price_drop">Mayor bajada de precio</option>
              <option value="price_m2_asc">Menor valor por m²</option>
            </SortSelect>
            <ViewButton type="button" $active={!compact} onClick={() => setCompact(false)} aria-label="Vista amplia"><FiGrid /></ViewButton>
            <ViewButton type="button" $active={compact} onClick={() => setCompact(true)} aria-label="Vista compacta"><FiList /></ViewButton>
            <MobileViewOptions>
              <button type="button" data-active={!mobileListOpen && !mobileMapOpen} onClick={() => { setMobileListOpen(false); setMobileMapOpen(false); }}><FiGrid /> Visual</button>
              <button type="button" data-active={mobileListOpen} onClick={() => { setMobileListOpen(true); setMobileMapOpen(false); }}><FiList /> Lista</button>
            </MobileViewOptions>
            <MapToggle type="button" onClick={() => { setMobileMapOpen((value) => !value); setMobileListOpen(false); }}><FiMap /> {mobileMapOpen ? 'Visual' : 'Mapa'}</MapToggle>
          </ViewControls>
        </ResultsHeader>

        <Layout $mapOpen={mobileMapOpen}>
          <Listings $compact={compact} $hiddenOnMobile={mobileMapOpen}>
            {loading && page === 1 && Array.from({ length: 4 }).map((_, index) => <Skeleton key={index} />)}

            {!loading && error && (
              <StateCard>
                <StateTitle>Algo interrumpió la búsqueda</StateTitle>
                <p>{error}</p>
                <Retry type="button" onClick={() => setReloadKey((value) => value + 1)}><FiRefreshCw /> Reintentar</Retry>
              </StateCard>
            )}

            {!loading && !error && properties.length === 0 && (
              <StateCard>
                <StateTitle>No encontramos coincidencias</StateTitle>
                <p>Probá ampliando la zona, el precio o quitando alguno de los filtros.</p>
              </StateCard>
            )}

            {!loading && !error && properties.map((property, index) => {
              const showService = index === 3 || index === 9;
              const ad = demoServiceAds[(index === 3 ? page * 2 - 2 : page * 2 - 1) % demoServiceAds.length];
              return (
                <PropertyResult key={property._id}>
                  {mobileListOpen ? <CompactPropertyRow property={property} detailQuery={property.operation === 'alquiler_vacacional' ? baseQueryString : ''} /> : <PropertyCard property={property} onHover={setHighlightedPropertyId} highlighted={String(property._id) === String(highlightedPropertyId)} detailQuery={property.operation === 'alquiler_vacacional' ? baseQueryString : ''} />}
                  {showService && <ServiceAdCard ad={ad} />}
                </PropertyResult>
              );
            })}
            {!error && hasMore && <LoadMoreSentinel ref={loadMoreRef} aria-live="polite">{loading ? 'Cargando más propiedades…' : `${properties.length} de ${total} propiedades`}</LoadMoreSentinel>}
          </Listings>

          <MapColumn $visibleOnMobile={mobileMapOpen}>
            <MapSticky>
              <MapLabel><FiMap /> Explorá las propiedades por ubicación</MapLabel>
              <MapFrame>
                {(desktopMap||mobileMapOpen)
                  ? <Suspense fallback={<MapFallback>Preparando mapa…</MapFallback>}><PropertyMap properties={properties} highlightedPropertyId={highlightedPropertyId} onPropertyHover={setHighlightedPropertyId} /></Suspense>
                  : <MapFallback>El mapa se carga cuando lo abras.</MapFallback>}
              </MapFrame>
            </MapSticky>
          </MapColumn>
        </Layout>
      </ResultsShell>
    </ResultsSection>
  );
}

export default PropertyList;

const ResultsSection = styled.section`
  min-height: 65vh;
  padding: 26px clamp(18px, 3vw, 48px) 80px;
  background: #f7f5ef;

  @media (max-width: 780px) {
    padding: 20px 14px 60px;
  }
`;
const PropertyResult=styled.div`display:contents;`;
const ResultsShell = styled.div`
  max-width: 1600px;
  margin: 0 auto;
`;
const ResultsHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 20px;

  @media (max-width: 700px) {
    align-items: stretch;
    flex-direction: column;
    gap: 16px;
  }
`;
const ResultsEyebrow = styled.div`
  margin-bottom: 4px;
  color: #b45f40;
  font-size:12px;
  font-weight: 750;
  letter-spacing: .16em;
`;
const ResultsTitle = styled.h2`
  margin: 0;
  color: #263f44;
  font-size: clamp(21px, 2vw, 30px);
  font-weight: 560;
  letter-spacing: -.035em;
`;
const ViewControls = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px;
  background: rgba(255,255,255,.8);
  border: 1px solid rgba(42,64,70,.11);
  border-radius: 999px;
  @media (max-width: 700px) {
    width: 100%;
    box-sizing: border-box;
    justify-content: space-between;
  }
`;
const SortSelect = styled.select`
  min-height: 38px;
  max-width: 220px;
  padding: 0 34px 0 13px;
  color: #29474f;
  background: transparent;
  border: 0;
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  &:focus-visible { outline: 2px solid rgba(201,106,70,.45); border-radius: 999px; }
  @media (max-width: 700px) { min-width: 0; max-width: none; flex: 1 1 auto; font-size: 13px; }
`;
const ViewButton = styled.button`
  display: grid;
  width: 38px;
  height: 38px;
  place-items: center;
  color: ${({$active}) => $active ? '#fff' : '#667579'};
  background: ${({$active}) => $active ? '#29474f' : 'transparent'};
  border: 0;
  border-radius: 50%;
  cursor: pointer;
  @media (max-width: 780px) { display: none; }
`;
const MobileViewOptions = styled.div`
  display:none;
  @media(max-width:780px){display:flex;align-items:center;gap:2px;padding:3px;background:#eef1ee;border-radius:999px;}
  button{display:inline-flex;min-height:32px;align-items:center;gap:5px;padding:0 9px;color:#627276;background:transparent;border:0;border-radius:999px;font:inherit;font-size:11px;font-weight:750;cursor:pointer;}
  button[data-active='true']{color:#fff;background:#29474f;}
  svg{font-size:13px;}
`;
const MapToggle = styled.button`
  display: none;
  min-height: 38px;
  align-items: center;
  gap: 7px;
  padding: 0 14px;
  color: #29474f;
  background: transparent;
  border: 0;
  font: inherit;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  @media (max-width: 780px) { display: flex; flex: 0 0 auto; min-width: 108px; justify-content: center; }
`;
const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, .92fr) minmax(440px, 1.08fr);
  align-items: start;
  gap: clamp(22px, 2.5vw, 42px);

  @media (max-width: 1050px) {
    grid-template-columns: minmax(0, 1fr) minmax(380px, .9fr);
  }
  @media (max-width: 780px) {
    display: block;
  }
`;
const Listings = styled.div`
  display: grid;
  grid-template-columns: ${({$compact}) => $compact ? 'repeat(2, minmax(0,1fr))' : 'minmax(0,1fr)'};
  gap: 20px;

  @media (max-width: 1220px) {
    grid-template-columns: minmax(0,1fr);
  }
  @media (max-width: 780px) {
    display: ${({$hiddenOnMobile}) => $hiddenOnMobile ? 'none' : 'grid'};
  }
`;
const LoadMoreSentinel=styled.div`
  display:grid;min-height:54px;place-items:center;color:#758589;font-size:12px;font-weight:700;text-align:center;
`;
const CompactLink = styled(Link)`
  display:grid;grid-template-columns:112px minmax(0,1fr) 22px;align-items:stretch;gap:12px;min-height:116px;padding:8px;color:inherit;background:#fff;border:1px solid rgba(42,64,70,.12);border-radius:14px;box-shadow:0 7px 18px rgba(42,64,70,.045);text-decoration:none;
  .open{align-self:center;color:#63757a;font-size:17px;}
  @media(min-width:781px){display:none;}
`;
const CompactImage = styled.div`
  display:grid;overflow:hidden;place-items:center;color:#718185;background:#e8e6df;border-radius:10px;
  img{width:100%;height:100%;object-fit:cover;}
`;
const CompactContent = styled.div`
  display:grid;min-width:0;align-content:center;gap:3px;
`;
const CompactMeta = styled.span`
  overflow:hidden;color:#b45f40;font-size:10px;font-weight:800;letter-spacing:.1em;text-overflow:ellipsis;text-transform:uppercase;white-space:nowrap;
`;
const CompactTitle = styled.h3`
  display:-webkit-box;margin:0;overflow:hidden;color:#29474f;font-size:15px;font-weight:750;letter-spacing:-.025em;line-height:1.2;-webkit-box-orient:vertical;-webkit-line-clamp:2;
`;
const CompactLocation = styled.div`
  display:grid;grid-template-columns:13px minmax(0,1fr);align-items:center;gap:4px;color:#718185;font-size:11px;
  svg{font-size:12px;color:#9aa5a6;}span,small{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}small{grid-column:2;color:#8b9799;font-size:10px;}
`;
const CompactFooter = styled.div`
  display:flex;align-items:baseline;justify-content:space-between;gap:6px;margin-top:2px;color:#6d7c80;font-size:10px;
  strong{min-width:0;overflow:hidden;color:#223b40;font-size:14px;letter-spacing:-.03em;text-overflow:ellipsis;white-space:nowrap;}strong small{font-size:9px;color:#758488;}
  >span{display:inline-flex;align-items:center;gap:3px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}svg{font-size:10px;}
`;
const MapColumn = styled.aside`
  min-width: 0;
  align-self: stretch;
  height: 100%;
  @media (max-width: 780px) { display: ${({$visibleOnMobile}) => $visibleOnMobile ? 'block' : 'none'}; }
`;
const MapSticky = styled.div`
  position: sticky;
  top: 124px;
`;
const MapLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  color: #344f55;
  font-size: 15px;
  font-weight: 680;
  letter-spacing: -.01em;

  svg {
    color: #b45f40;
    font-size: 17px;
  }
`;
const MapFrame = styled.div`
  height: min(650px, calc(100vh - 160px));
  min-height: 440px;
  overflow: hidden;
  background: #e8edea;
  border: 7px solid rgba(255,255,255,.9);
  border-radius: 26px;
  box-shadow: 0 24px 65px rgba(42,64,70,.12), 0 0 0 1px rgba(42,64,70,.07);
  @media (max-width: 780px) { height: calc(100dvh - 210px); min-height: 420px; border-width: 5px; border-radius: 20px; }
`;
const MapFallback=styled.div`display:grid;width:100%;height:100%;min-height:300px;place-items:center;padding:24px;background:#e8edea;color:#6c7c7f;font-size:13px;font-weight:700;text-align:center`;
const Skeleton = styled.div`
  height: 570px;
  border-radius: 14px;
  background: linear-gradient(100deg, #e8e5de 20%, #f2f0ea 35%, #e8e5de 50%);
  background-size: 300% 100%;
  animation: shimmer 1.5s infinite;
  @keyframes shimmer { to { background-position-x: -150%; } }
`;
const StateCard = styled.div`
  display: grid;
  min-height: 280px;
  place-content: center;
  padding: 36px;
  color: #667579;
  text-align: center;
  background: rgba(255,255,255,.72);
  border: 1px solid rgba(42,64,70,.1);
  border-radius: 16px;
  p { max-width: 430px; margin: 8px auto 20px; line-height: 1.6; }
`;
const StateTitle = styled.h3`
  margin: 0;
  color: #29474f;
  font-size: 22px;
  font-weight: 600;
`;
const Retry = styled.button`
  display: inline-flex;
  min-height: 44px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 0 auto;
  padding: 0 18px;
  color: #fff;
  background: #b45f40;
  border: 0;
  border-radius: 999px;
  font-weight: 700;
  cursor: pointer;
`;
